import { getTokenManager, type TokenManager } from '@meisterwerk/fe-authentication-sdk'
import { getQueryClient, resetForNewUser } from '../api/query-client'
import { isEmbeddedPage } from './embedding'
import { SessionUser } from './session-user'

type Manager = Pick<TokenManager, 'ensureFreshToken' | 'isAuthenticated' | 'getAccessToken'>

interface SessionSourceDeps {
  getManager: () => Manager
  /** Whether a host app embeds fe-inv; a getter, as the mark of a page of its own is read late */
  isEmbedded: () => boolean
  /** Runs before the first token of another user than the last one leaves */
  onUserChanged: () => void
}

/**
 * The one place a token leaves fe-inv's SDK manager (INV-927). Embedded, an empty shared set means
 * the PWA signed out, and nothing refreshes: after a sign-out whose logout failed, such a refresh
 * would sign the browser back in with the refresh cookie the logout left.
 */
export class SessionSource {
  private readonly user = new SessionUser()

  constructor(private readonly deps: SessionSourceDeps) {}

  /** Embedded, with no access token stored: the host signed out. The empty-set rule, stated once */
  isSignedOutHere(): boolean {
    return this.deps.isEmbedded() && this.storedToken() === null
  }

  /** The SDK's fresh token, refreshed when it must be; null, nothing sent, once signed out here */
  async freshToken(): Promise<string | null> {
    if (this.isSignedOutHere()) return null
    return this.exit(await this.deps.getManager().ensureFreshToken())
  }

  /** The stored token while it is valid, never refreshed: a request's in a connection problem */
  storedValidToken(): string | null {
    const manager = this.deps.getManager()
    return this.exit(manager.isAuthenticated() ? manager.getAccessToken() : null)
  }

  /** The stored access token, valid or not: for checks and a render, never sent */
  storedToken(): string | null {
    return this.deps.getManager().getAccessToken()
  }

  /** The one way out for a token: another user's than the last resets the previous user's data */
  private exit(token: string | null): string | null {
    if (token !== null && this.user.changesTo(token)) this.deps.onUserChanged()
    return token
  }
}

/** fe-inv's SDK manager, as auth-init.ts starts it, and the app's query cache */
export const sessionSource = new SessionSource({
  getManager: getTokenManager,
  isEmbedded: isEmbeddedPage,
  onUserChanged: () => void resetForNewUser(getQueryClient()),
})
